import React, { ReactElement, useCallback, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
  BreadcrumbGroup,
  BreadcrumbGroupProps,
  SpaceBetween,
  Tabs,
} from '@amzn/awsui-components-react-v3/polaris';
import { NonCancelableCustomEvent } from '@amzn/awsui-components-react-v3/uxdg';
import { TabsProps } from '@amzn/awsui-components-react-v3/polaris/tabs';
import { useTranslation } from 'react-i18next';
import MoveContainer from '../MoveContainer';
import ManageContainer from '../ManageContainer';
import { moveContainerFeatureFlag } from '../../../utils/featureFlags';

/**
 * match.path from RouteComponentProps will have the current browser path
 */
export type ContainerHomeProps = RouteComponentProps;

enum CMSHomeTabId {
  ContainerMove = 'containerMove',
  ContainerManage = 'manageContainers',
}

/**
 * Component for CMS Page
 * @component
 * @param {ContainerManagementProps} props
 */
const ContainerHome = ({ match }: ContainerHomeProps): ReactElement => {
  const { t } = useTranslation();
  const landingTabId = match.path.endsWith('/move/')
    ? CMSHomeTabId.ContainerMove
    : CMSHomeTabId.ContainerManage;
  const defaultBreadcrumbs = [
    {
      text: t('breadcrumb.home'),
      href: '/',
    },
    {
      text: t('Inventory Management'),
      href: '/assets-inventory/',
    },
    {
      text: t('Container Management'),
      href: '/container/manage/',
    },
  ];
  const [selectedTabId, setSelectedTabId] = useState(landingTabId);
  const [breadcrumb, setBreadcrumb] = useState(defaultBreadcrumbs);

  const onTabChangeCallback = useCallback(
    (event: NonCancelableCustomEvent<TabsProps.ChangeDetail>) =>
      setSelectedTabId(event.detail.activeTabId as CMSHomeTabId),
    []
  );

  const onBreadcrumbChangeCallback = useCallback(
    (newBreadcrumb: BreadcrumbGroupProps.Item[]) => setBreadcrumb(newBreadcrumb),
    []
  );

  const showMoveContainerTool = moveContainerFeatureFlag();
  const tabs = [
    {
      label: 'Manage containers',
      id: CMSHomeTabId.ContainerManage,
      content: <ManageContainer onBreadcrumbChange={onBreadcrumbChangeCallback} />,
    },
  ];
  if (showMoveContainerTool) {
    tabs.push({
      label: 'Container move',
      id: CMSHomeTabId.ContainerMove,
      content: <MoveContainer onBreadcrumbChange={onBreadcrumbChangeCallback} />,
    });
  }

  return (
    <SpaceBetween direction="vertical" size="s">
      <BreadcrumbGroup items={breadcrumb} />
      <Tabs
        data-test-id="tab-home"
        tabs={tabs}
        activeTabId={selectedTabId}
        variant="default"
        onChange={onTabChangeCallback}
      />
    </SpaceBetween>
  );
};

export default ContainerHome;
